import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  Zap,
  Eye
} from "lucide-react";
import { getWorkloadMetrics, getAutoApprovalTrends } from "@/services/workloadAnalyticsService";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export const ComplianceWorkloadDashboard = () => {
  const [liveUpdateCount, setLiveUpdateCount] = useState(0);

  const { data: metrics, refetch: refetchMetrics } = useQuery({
    queryKey: ["workload-metrics"],
    queryFn: getWorkloadMetrics,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const { data: trends } = useQuery({
    queryKey: ["auto-approval-trends"],
    queryFn: getAutoApprovalTrends,
  });

  // Real-time subscription for auto-approvals
  useEffect(() => {
    const channel = supabase
      .channel('workload-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ai_decision_logs',
          filter: 'decision_type=eq.auto_approval'
        },
        () => {
          setLiveUpdateCount(prev => prev + 1);
          refetchMetrics();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetchMetrics]);

  if (!metrics) {
    return <div className="flex items-center justify-center p-8">Loading workload metrics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Live Update Indicator */}
      {liveUpdateCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          <span>Live updates active • {liveUpdateCount} new auto-approvals</span>
        </div>
      )}

      {/* Primary Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Auto-Approved Today</p>
              <p className="text-3xl font-bold mt-2">{metrics.autoApprovedToday}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics.autoApprovedThisWeek} this week
              </p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Hours Saved Today</p>
              <p className="text-3xl font-bold mt-2">{metrics.hoursSavedToday.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics.hoursSavedThisWeek.toFixed(1)} hours this week
              </p>
            </div>
            <Clock className="h-8 w-8 text-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pending High Risk</p>
              <p className="text-3xl font-bold mt-2 text-destructive">{metrics.pendingHighRisk}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Requires immediate review
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Auto-Approval Rate</p>
              <p className="text-3xl font-bold mt-2">{metrics.autoApprovalRate.toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics.totalPending} deals pending
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-primary" />
          </div>
        </Card>
      </div>

      {/* Pending Queue Analysis */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Pending Queue by Risk Level</h3>
            <Badge variant="outline">{metrics.totalPending} Total</Badge>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium">High Risk (≥70)</p>
                  <p className="text-xs text-muted-foreground">Immediate attention required</p>
                </div>
              </div>
              <Badge variant="destructive">{metrics.pendingHighRisk}</Badge>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
                <div>
                  <p className="font-medium">Medium Risk (40-69)</p>
                  <p className="text-xs text-muted-foreground">Standard review process</p>
                </div>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-700 dark:text-yellow-400">
                {metrics.pendingMediumRisk}
              </Badge>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500" />
                <div>
                  <p className="font-medium">Low Risk (&lt;40)</p>
                  <p className="text-xs text-muted-foreground">Low priority review</p>
                </div>
              </div>
              <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                {metrics.pendingLowRisk}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Weekly Trend Chart */}
      <Card className="p-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Auto-Approval Trend (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trends || []}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => format(new Date(date), 'MMM d')}
                className="text-xs"
              />
              <YAxis className="text-xs" />
              <Tooltip 
                labelFormatter={(date) => format(new Date(date), 'MMM d, yyyy')}
                formatter={(value: number) => [value, 'Auto-Approved']}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                dot={{ fill: "hsl(var(--primary))" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* AI Performance Insights */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <Zap className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Avg Processing Time</p>
              <p className="text-2xl font-bold">{metrics.avgAutoApprovalTime.toFixed(1)}s</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Automation Rate</p>
              <p className="text-2xl font-bold">{metrics.autoApprovalRate.toFixed(0)}%</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <Eye className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Manual Reviews</p>
              <p className="text-2xl font-bold">{metrics.manualReviewRequired}</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
